import React, { useState, useEffect } from 'react';
import { fetchAuthenticatedImageWithCache, DEFAULT_PATIENT_PLACEHOLDER } from '../utils/imageUtils';
import { FaUser } from 'react-icons/fa';

const AuthenticatedImage = ({ 
  patientId, 
  imagePath = "", 
  alt = "Participant Image", 
  className = "",
  size = null,
  circle = false,
  borderWidth = 2,
  borderColor = "#ddd",
  onError = null,
  ...props 
}) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadImage = async () => {
      if (!patientId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(false);
        
        const blobUrl = await fetchAuthenticatedImageWithCache(patientId, imagePath);
        
        if (isMounted) {
          setImageUrl(blobUrl);
        }
      } catch (err) {
        console.error('Error loading authenticated image:', err);
        if (isMounted) {
          setError(true);
          if (onError) onError(err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadImage();

    return () => {
      isMounted = false;
    };
  }, [patientId, imagePath, onError]);

  // Create styles for circle
  const circleStyle = circle ? {
    width: size ? `${size}px` : '24px',
    height: size ? `${size}px` : '24px',
    borderRadius: '50%',
    border: `${borderWidth}px solid ${borderColor}`,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f8f9fa',
    objectFit: 'cover'
  } : {};

  const imageStyle = circle ? {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: '50%'
  } : {};

  if (loading) {
    return (
      <div 
        className={`image-loading ${className}`} 
        style={circle ? circleStyle : {}}
        {...props}
      >
        {circle ? (
          <FaUser style={{ fontSize: size ? `${size * 0.4}px` : '10px', color: '#6c757d' }} />
        ) : (
          <div className="loading-spinner">Loading...</div>
        )}
      </div>
    );
  }

  if (error || !imageUrl) {
    return circle ? (
      <div 
        className={`image-error ${className}`}
        style={circleStyle}
        {...props}
      >
        <FaUser style={{ fontSize: size ? `${size * 0.4}px` : '10px', color: '#6c757d' }} />
      </div>
    ) : (
      <img 
        src={DEFAULT_PATIENT_PLACEHOLDER} 
        alt={alt} 
        className={`image-error ${className}`}
        {...props}
      />
    );
  }

  return circle ? (
    <div style={circleStyle} className={className} {...props}>
      <img 
        src={imageUrl} 
        alt={alt} 
        style={imageStyle}
        onError={() => setError(true)}
      />
    </div>
  ) : (
    <img 
      src={imageUrl} 
      alt={alt} 
      className={className}
      onError={() => setError(true)}
      {...props}
    />
  );
};

export default AuthenticatedImage;